// Terminal-state subview for PhantomSlipPanel (post-resolve).
// Surfaces the simulated outcome with explicit Phase-1A safety copy:
//   - "Simulated result"
//   - "No real match result was used."
//   - "No real money was involved."
// Per docs/phantom-resolution-source-of-truth.md §6.
//
// 9C.10c: terminal cards spread cardLow + transitionStandard via the
// new sibling rails file; eyebrow rows gain a registered icon
// (lifecycle.complete for won, process.process_warning for busted).
// Won/busted background distinction preserved by the parent
// terminalCardWon / terminalCardBusted backgrounds.

import type { CSSProperties } from "react";

import { Icon } from "../../components/icons/Icon.tsx";
import type { PhantomSelection } from "../../phantom/usePhantomCycle.ts";
import * as d from "./dashboard.styles.ts";
import * as p from "./phantom-slip-panel.styles.ts";
import * as r from "./phantom-terminal-note-rails.styles.ts";

export interface PhantomResultPayload {
  bust_leg_index?: number | null;
  bust_fixture_id?: string | null;
  leg_results?: Array<{ leg_index?: number; fixture_id?: string; outcome?: string }>;
}

interface PhantomTerminalNoteProps {
  status: "phantom_won" | "phantom_busted";
  selections: PhantomSelection[];
  result: PhantomResultPayload;
}

const cardWonStyle: CSSProperties = { ...p.terminalCardWon, ...r.cardRails };
const cardBustedStyle: CSSProperties = { ...p.terminalCardBusted, ...r.cardRails };

export function PhantomTerminalNote({
  status,
  selections,
  result,
}: PhantomTerminalNoteProps): JSX.Element {
  if (status === "phantom_won") {
    return (
      <div style={cardWonStyle}>
        <span style={r.headerLead}>
          <Icon name="lifecycle.complete" size="sm" decorative />
          <span style={p.terminalEyebrowWon}>Simulated result</span>
        </span>
        <span style={d.cardTitle}>Won (simulated)</span>
        <span>No real match result was used.</span>
        <span>No real money was involved.</span>
        <span>This is Phase 1A scaffolding — real-stake recommendations remain disabled.</span>
      </div>
    );
  }
  // phantom_busted — surface the first losing fixture's label, marked simulated.
  const idx = result.bust_leg_index ?? null;
  const bustSelection = idx !== null && idx >= 0 ? selections[idx] : undefined;
  const bustLabel =
    bustSelection?.home_team !== undefined &&
    bustSelection?.home_team !== null &&
    bustSelection?.away_team !== undefined &&
    bustSelection?.away_team !== null
      ? `${bustSelection.home_team} vs ${bustSelection.away_team}`
      : (result.bust_fixture_id ?? "(fixture)");
  return (
    <div style={cardBustedStyle}>
      <span style={r.headerLead}>
        <Icon name="process.process_warning" size="sm" decorative />
        <span style={p.terminalEyebrowBusted}>Simulated result</span>
      </span>
      <span style={d.cardTitle}>Busted (simulated)</span>
      <span style={p.bustLegLine}>First losing leg: {bustLabel} (simulated)</span>
      <span>No real match result was used.</span>
      <span>No real money was involved.</span>
      <span>This is Phase 1A scaffolding — real-stake recommendations remain disabled.</span>
    </div>
  );
}
